import { Dispatch } from "react-redux";
import { ActionTypes } from "./action-types";
import { AuthActionCreators, IApplicationState } from "../";
import {
    IDonorRegistration, ICenterSummary, ICenter, ICenterSaturation, IProductOwner, IBaseResponse, IClsOverrides, IWaitlistAllocationConfig,
    ICenterBase, ICenterAddress, IListItem, IReferralConfig, IReferralConfigRequest,
    ICenterFollowUpLabOption,
    IClsOverridesRequest, IWaitlistAllocationConfigRequest,
    IAdminRelativePerformance,
    ICenterAddressContract,
} from "@nkr/models/index";
import { IGet, IPost } from "../authorization";

import {
    ActionCreators as ActionCreatorsOptnSrtrData, IOptnSrtrDataUploadResponse, IOptnSrtrDataValidateResponse,
    IOptnSrtrDataProcessResponse, IOptnSrtrDataProgressResponse, IOptnSrtrMonthlyDataResponse, IUpdateOptnSrtrData, IOptnSrtrMonthlyDataAddResponse
} from './actions-optn-srtr-data';

import {
    ActionCreatorsCenterProfile, ICenterAdminDataResponse, ICenterUnosDataResponse, ICenterSrtrDataResponse, ICenterProfileResponse, ICenterProfileLookupDataResponse, ICenterLabDataResponse,
    ICenterServicesDataResponse, ICenterSerologyDataResponse, ICenterAnatomyPrefsDataResponse, ICenterLegalDocumentsResponse, IManualInvoiceLineItemLookupResponse, IManualInvoiceLineItemTaxResponse,
    ICenterPackagingDataResponse, OrganPackaging, ICenterProfileLookupData, initialCenterProfileLookupData
} from './actions-center-profile';
import { IDashCenterLookupDataResponse } from "../donor-intake/actions";
import { OrganType } from '@nkr/enums/organ-type';
import { seedAdvReimburseEnabledCenters } from '@nkr/views/donors/watr/components/adv-reimburse-enabled-centers';

export enum CenterEmailType {
    CenterPerformance = 1,
    ActionCenterDigest = 2,
    MicrositeDailySummary = 3
}

export interface ICenterDashListResponse {
    data: {
        success: boolean,
        results: IDonorRegistration[]
    }
}

export interface ICenterSummaryListResponse {
    data: {
        success: boolean,
        results: ICenterSummary[]
    }
}

export interface IClsOverrideResponse {
    data: {
        success: boolean,
        results: IClsOverrides
    }
}

export interface IWaitListAllocationResponse {
    data: {
        success: boolean,
        results: IWaitlistAllocationConfig
    }
}

export interface IReferralConfigResponse {
    data: {
        success: boolean,
        results: IReferralConfig
    }
}

export interface ICenterListResponse {
    data: {
        success: boolean,
        results: ICenter[]
    }
}

export interface ICenterSaturationListResponse {
    data: {
        success: boolean,
        results: ICenterSaturation[]
    }
}
export interface IProductOwnerResponse {
    data: {
        success: boolean,
        results: Record<string, IProductOwner>
    }
}

export interface IBasicCenterListResponse {
    data: {
        success: boolean,
        results: ICenterBase[]
    }
}

export interface ICenterAddressListResponse {
    data: {
        success: boolean,
        results: ICenterAddress[]
    }
}

export interface ICenterAddressResponse {
    data: {
        success: boolean,
        results: ICenterAddress
    }
}

export interface ICenterAddressLookupDataResponse {
    data: {
        success: boolean,
        results: {
            addressTypeList: IListItem[],
            stateList: IListItem[]
        }
    }
}

export interface IAddUpdateCenterAddressResponse {
    data: {
        success: boolean,
        message: string,
        results: ICenterAddress
    }
}

export interface ICenterFollowUpLabOptionListResponse {
    data: {
        success: boolean,
        results: ICenterFollowUpLabOption[]
    }
}

export interface IAddUpdateFollowUpLabOptionResponse {
    data: {
        success: boolean,
        message: string,
        results: ICenterFollowUpLabOption
    }
}

export interface IAdminRelativePerformanceResponse {
    data: {
        success: boolean,
        results: IAdminRelativePerformance[]
    }
}

export interface IReferralConfigLookupDataResponse {
    data: {
        success: boolean,
        message: string,
        results: {
            statusList: IListItem[],
            followUpList: IListItem[],
            diabetesStatusList: IListItem[],
            customKitsList: IListItem[],
            preWorkupLabConfigList: IListItem[],
            cancerTypeList: IListItem[],
            fvVideoList: IListItem[],
            svVideoList: IListItem[],
            donorConnectGroups: IListItem[]
        }
    }
}

export interface IBulkCheekSwabDataResponse {
    data: {
        success: boolean,
        message: string,
        results: {
            firstName: string,
            lastName: string
            address1: string,
            address2: string,
            city: string,
            state: string,
            postalCode: string,
            contactPhone: string,
            email: string,
            dob: string
        }
    }
}

export interface IBulkCheekSwabRequest {
    firstName: string,
    lastName: string,
    address1: string,
    address2: string,
    city: string,
    state: string,
    postalCode: string
}

const ActionCreatorsMain = {
    getCenterSummaryList: () => (dispatch: Dispatch<IApplicationState>): Promise<ICenterSummaryListResponse> => {
        let options = {
            url: __API__ + "/center/summary"
        };

        return dispatch(AuthActionCreators.apiGet(options)).then((response: ICenterSummaryListResponse) => {
            if (response.data.success)
                dispatch(ActionCreators.setCenterSummaryList(response.data.results));

            return Promise.resolve(response);
        });
    },
    setCenterSummaryList: (list: ICenterSummary[]) => {
        return {
            type: ActionTypes.SET_CENTER_SUMMARY_LIST,
            list
        };
    },

    getCenterList: () => (dispatch: Dispatch<IApplicationState>): Promise<ICenterListResponse> => {
        let options = {
            url: __API__ + "/center"
        };

        return dispatch(AuthActionCreators.apiGet(options)).then((response: ICenterListResponse) => {
            if (response.data.success)
                dispatch(ActionCreators.setCenterList(response.data.results));

            return Promise.resolve(response);
        });
    },
    setCenterList: (list: ICenter[]) => {
        return {
            type: ActionTypes.SET_CENTER_LIST,
            list
        };
    },

    getBasicCenterList: (liverOnly: boolean) => (dispatch: Dispatch<IApplicationState>): Promise<IBasicCenterListResponse> => {
        let options = {
            url: `${__API__}/center/basic?liverOnly=${liverOnly}`
        };

        return dispatch(AuthActionCreators.apiGet(options)).then((response: IBasicCenterListResponse) => {
            if (response && response.data && response.data.success) {
                seedAdvReimburseEnabledCenters(response.data.results);
            }
            return Promise.resolve(response);
        });
    },

    getDonorIntakeLookupData: (centerId: number) => (dispatch: Dispatch<IApplicationState>): Promise<IDashCenterLookupDataResponse> => {
        let options = {
            url: `${__API__}/center/${centerId}/dash/lookup-data`,
        };
        try {
            return dispatch(AuthActionCreators.apiGet(options)).then((response: IDashCenterLookupDataResponse) => {
                return Promise.resolve(response);
            });
        } catch (error) {
            throw error;
        }
    },

    getBaseCentersOnly: () => async (dispatch: Dispatch<IApplicationState>): Promise<ICenterListResponse> => {
        let config: IGet = {
            url: `${__API__}/center/base`
        };

        const response = await dispatch(AuthActionCreators.apiGet(config));

        return await Promise.resolve(response);
    },

    getCenterSaturationList: () => (dispatch: Dispatch<IApplicationState>): Promise<ICenterSaturationListResponse> => {
        let options = {
            url: `${__API__}/center/saturation`
        };

        return dispatch(AuthActionCreators.apiGet(options)).then((response: ICenterSaturationListResponse) => {
            if (response.data.success)
                dispatch(ActionCreators.setCenterSaturationList(response.data.results));

            return Promise.resolve(response);
        });
    },
  
    setCenterSaturationList: (list: ICenterSaturation[]) => {
        return {
            type: ActionTypes.SET_CENTER_SATURATION_LIST,
            list
        };
    },

    getProductOwners: () => (dispatch: Dispatch<IApplicationState>): Promise<IProductOwnerResponse> => {
        let options = {
            url: __API__ + "/center/product-owners"
        };

        return dispatch(AuthActionCreators.apiGet(options)).then((response: IProductOwnerResponse) => {
            if (response.data.success)
                dispatch(ActionCreators.setProductOwnerList(response.data.results));

            return Promise.resolve(response);
        });
    },
    setProductOwnerList: (productOwners: Record<string, IProductOwner>) => {
        return {
            type: ActionTypes.SET_PRODUCT_OWNER_LIST,
            productOwners
        };
    },

    getClsOverrides: (id: number) => (dispatch: Dispatch<IApplicationState>): Promise<IClsOverrideResponse> => {
        let options = {
            url: `${__API__}/center/${id}/cls-override`
        };

        return dispatch(AuthActionCreators.apiGet(options)).then((response: IClsOverrideResponse) => {
            return Promise.resolve(response);
        });
    },
    saveClsOverrides: (data: IClsOverridesRequest) => (dispatch: Dispatch<IApplicationState>): Promise<IBaseResponse> => {
        let options = {
            url: `${__API__}/center/cls-overrides`,
            data
        };

        return dispatch(AuthActionCreators.apiPost(options)).then((response: IBaseResponse) => {
            return Promise.resolve(response);
        });
    },

    saveCenterNotes: (centerId: number, notes: string) => (dispatch: Dispatch<IApplicationState>): Promise<IBaseResponse> => {
        let options = {
            url: `${__API__}/center/${centerId}/notes`,
            data: JSON.stringify(notes)
        };

        return dispatch(AuthActionCreators.apiPut(options)).then((response: IBaseResponse) => {
            if (response.data.success)
                dispatch(ActionCreators.updateCenterNotes(centerId, notes));

            return Promise.resolve(response);
        });
    },
    updateCenterNotes: (centerId: number, notes: string) => {
        return {
            type: ActionTypes.UPDATE_CENTER_NOTES_FOR_SAT,
            centerId,
            notes
        };
    },

    saveCenterCategory: (centerId: number, category: string) => (dispatch: Dispatch<IApplicationState>): Promise<IBaseResponse> => {
        let options = {
            url: `${__API__}/center/${centerId}/category`,
            data: JSON.stringify(category)
        };

        return dispatch(AuthActionCreators.apiPut(options)).then((response: IBaseResponse) => {
            if (response.data.success)
                dispatch(ActionCreators.updateCenterCategory(centerId, category));

            return Promise.resolve(response);
        });
    },
    updateCenterCategory: (centerId: number, category: string) => {
        return {
            type: ActionTypes.UPDATE_CENTER_CATEGORY,
            centerId,
            category
        };
    },

    saveCenterSaturationAnnualGoal: (centerId: number, goal: number) => (dispatch: Dispatch<IApplicationState>): Promise<IBaseResponse> => {
        let options = {
            url: `${__API__}/center/${centerId}/saturation-annual-goal`,
            data: JSON.stringify(goal)
        };

        return dispatch(AuthActionCreators.apiPut(options)).then((response: IBaseResponse) => {
            // if (response.data.success)
            //     dispatch(ActionCreators.updateCenterSaturationAnnualGoal(centerId, goal));

            return Promise.resolve(response);
        });
    },
    updateCenterSaturationAnnualGoal: (centerId: number, goal: number) => {
        return {
            type: ActionTypes.UPDATE_CENTER_SATURATION_GOAL,
            centerId,
            goal
        };
    },

    getCenterAddressList: (centerId: number) => (dispatch: Dispatch<IApplicationState>): Promise<ICenterAddressListResponse> => {
        let options = {
            url: __API__ + '/center/' + centerId + '/addresses'
        };

        return dispatch(AuthActionCreators.apiGet(options)).then((response: ICenterAddressListResponse) => {
            return Promise.resolve(response);
        });
    },
    updateCenterAddress: (data: ICenterAddressContract) => async (dispatch: Dispatch<IApplicationState>): Promise<IAddUpdateCenterAddressResponse> => {
        let options = {
            url: `${__API__}/center/address`,
            data: data
        };

        return await dispatch(AuthActionCreators.apiPut(options));
    },

    getCenterAddresses: (centerId: number, organType: OrganType) => async (dispatch: Dispatch<IApplicationState>): Promise<ICenterAddressListResponse> => {
        let config: IGet = {
            url: `${__API__}/center/${centerId}/addresses?organType=${organType}`
        };

        const response = await dispatch(AuthActionCreators.apiGet(config));
        return response;
    },

    getCenterAddressLookupData: (organType: OrganType) => async (dispatch: Dispatch<IApplicationState>): Promise<ICenterAddressLookupDataResponse> => {
        let config: IGet = {
            url: `${__API__}/center/address/lookup-data?organType=${organType}`
        };

        const response = await dispatch(AuthActionCreators.apiGet(config));
        return response;
    },
    addCenterAddress: (data:ICenterAddressContract) => async (dispatch: Dispatch<IApplicationState>): Promise<IAddUpdateCenterAddressResponse> => {
        let options = {
            url: `${__API__}/center/address`,
            data: data
        };

        return await dispatch(AuthActionCreators.apiPost(options));
    },

    deleteCenterAddress: (id: number) => (dispatch: Dispatch<IApplicationState>): Promise<IBaseResponse> => {
        let options = {
            url: `${__API__}/center/address/${id}`
        };

        return dispatch(AuthActionCreators.apiDelete(options)).then((response: IBaseResponse) => {
            return response;
        });
    },

    getWaitListConfig: (id: number) => (dispatch: Dispatch<IApplicationState>): Promise<IWaitListAllocationResponse> => {
        let options = {
            url: `${__API__}/center/${id}/waitlist-allocation-config`
        };

        return dispatch(AuthActionCreators.apiGet(options)).then((response: IWaitListAllocationResponse) => {
            return Promise.resolve(response);
        });
    },
    saveWaitListConfig: (centerId: number, data: IWaitlistAllocationConfigRequest) => (dispatch: Dispatch<IApplicationState>): Promise<IBaseResponse> => {
        let options = {
            url: `${__API__}/center/${centerId}/update-waitlist-allocation-config`,
            data
        };

        return dispatch(AuthActionCreators.apiPut(options)).then((response: IBaseResponse) => {
            return Promise.resolve(response);
        });
    },

    getReferralConfig: (centerId: number) => (dispatch: Dispatch<IApplicationState>): Promise<IReferralConfigResponse> => {
        let options = {
            url: `${__API__}/center/${centerId}/referral-config`
        };

        return dispatch(AuthActionCreators.apiGet(options)).then((response: IReferralConfigResponse) => {
            return Promise.resolve(response);
        });
    },

    saveReferralConfig: (centerId: number, data: IReferralConfigRequest) => (dispatch: Dispatch<IApplicationState>): Promise<IBaseResponse> => {
        let options = {
            url: `${__API__}/center/${centerId}/update-referral-config`,
            data
        };

        return dispatch(AuthActionCreators.apiPut(options)).then((response: IBaseResponse) => {
            return Promise.resolve(response);
        });
    },

    getLookupData: (centerId: number) => async (dispatch: Dispatch<IApplicationState>): Promise<IReferralConfigLookupDataResponse> => {
        let options = {
            url: `${__API__}/center/${centerId}/referral-lookup-data`
        };

        return dispatch(AuthActionCreators.apiGet(options)).then((response: IReferralConfigLookupDataResponse) => {
            return Promise.resolve(response);
        });
    },

    sendEmail: (centerId: number, emailType: CenterEmailType, additionalOptions: { historyCompletedDays: number }) => (dispatch: Dispatch<IApplicationState>): Promise<IBaseResponse> => {
        let options = {
            url: `${__API__}/email/center/${centerId}?emailType=${emailType}&historyCompletedDays=${additionalOptions.historyCompletedDays}`,
        };

        return dispatch(AuthActionCreators.apiGet(options)).then((response: IBaseResponse) => {
            return Promise.resolve(response);
        });
    },

    setAddAddress: (addAddress: boolean) => {
        return {
            type: ActionTypes.SET_ADD_ADDRESS,
            addAddress
        };
    },

    getCenterAddressesDownload: (donorCenterId: number, recipCenterId: number, clusterId: number) => (dispatch: Dispatch<IApplicationState>): Promise<any> => {
        let options: IGet = {
            url: __API__ + `/center/downloadaddress/?donorCenterId=${donorCenterId}&recipCenterId=${recipCenterId}&clusterId=${clusterId}`,
            responseType: 'blob'
        };
        return dispatch(AuthActionCreators.apiGet(options));
    },

    setAddFollowUpLabOptions: (addFollowUpLabOption: boolean) => {
        return {
            type: ActionTypes.SET_ADD_FOLLOWUP_LAB_OPTION,
            addFollowUpLabOption
        };
    },

    updateCenterFollowUpLabOptions: (id: number, isFollowUpNeeded: boolean, isLabOrdersOrPrintableLetters: boolean, optionText: string, filePath: string, labOrderFile: File) => (dispatch: Dispatch<IApplicationState>): Promise<IAddUpdateFollowUpLabOptionResponse> => {
        let formData = new FormData
        formData.append('id', id.toString());
        formData.append('isFollowUpNeeded', isFollowUpNeeded.toString());
        formData.append('isLabOrdersOrPrintableLetters', isLabOrdersOrPrintableLetters.toString());
        formData.append('optionText', optionText);
        formData.append('filePath', filePath);
        formData.append('labOrderFile', labOrderFile);

        let options = {
            url: `${__API__}/center/fu-lab-option`,
            data: formData
        };

        return dispatch(AuthActionCreators.apiPut(options)).then((response: IAddUpdateFollowUpLabOptionResponse) => {
            return Promise.resolve(response);
        });
    },

    getCenterFollowUpLabOptions: (centerId: number) => async (dispatch: Dispatch<IApplicationState>): Promise<ICenterFollowUpLabOptionListResponse> => {
        let config: IGet = {
            url: `${__API__}/center/${centerId}/fu-lab-option`
        };

        let response = await dispatch(AuthActionCreators.apiGet(config));
        return response;
    },

    addCenterFollowUpLabOptions: (id: number, centerId: number, isFollowUpNeeded: boolean, isLabOrdersOrPrintableLetters: boolean, optionText: string, filePath: string, labOrderFile: File) => (dispatch: Dispatch<IApplicationState>): Promise<ICenterFollowUpLabOptionListResponse> => {
        let formData = new FormData
        formData.append('id', id.toString());
        formData.append('centerId', centerId.toString());
        formData.append('isFollowUpNeeded', isFollowUpNeeded.toString());
        formData.append('isLabOrdersOrPrintableLetters', isLabOrdersOrPrintableLetters.toString());
        formData.append('optionText', optionText);
        formData.append('filePath', filePath);
        formData.append('labOrderFile', labOrderFile);

        let options = {
            url: `${__API__}/center/${centerId}/fu-lab-option`,
            data: formData
        };

        return dispatch(AuthActionCreators.apiPostFormData(options)).then((response: ICenterFollowUpLabOptionListResponse) => {
            return Promise.resolve(response);
        });
    },

    deleteCenterFollowUpLabOptions: (id: number, centerId: number) => (dispatch: Dispatch<IApplicationState>): Promise<IBaseResponse> => {
        let options = {
            url: `${__API__}/center/${centerId}/fu-lab-option/${id}`
        };

        return dispatch(AuthActionCreators.apiDelete(options)).then((response: IBaseResponse) => {
            return response;
        });
    },

    uploadFollowUpFile: (followUpDonorLabOptionId: number, centerId: number, labOrderFile: File) => async (dispatch: Dispatch<IApplicationState>): Promise<IBaseResponse> => {
        let formData = new FormData();

        formData.append('labOrderFile', labOrderFile);

        let config: IPost = {
            url: `${__API__}/center/${centerId}/fu-lab-option/${followUpDonorLabOptionId}/upload`,
            data: formData
        };

        return await dispatch(AuthActionCreators.apiPostFormData(config)).then((response: IBaseResponse) => {
            return response;
        });
    },

    getLabOrderPreview: (labOrderId: number, centerId: number) => (dispatch: Dispatch<IApplicationState>) => {
        let options: IGet = {
            url: `${__API__}/center/${centerId}/fu-lab-option/${labOrderId}/preview-file`,
            responseType: 'blob'
        };

        return dispatch(AuthActionCreators.apiGet(options));
    },

    getAdminRelativePerformance: () => (dispatch: Dispatch<IApplicationState>) => {
        let options: IGet = {
            url: `${__API__}/report/relative-performance`,
        };

        return dispatch(AuthActionCreators.apiGet(options));
    },

    sendEmergencyNotification: (recipientId: number, message: string, centerId: number, tempOrPermanent: number) => async (dispatch: Dispatch<IApplicationState>): Promise<IBaseResponse> => {
        let options: IPost = {
            url: `${__API__}/center/send-emergency-notification`,
            data: {
                recipientId,
                message,
                centerId,
                tempOrPermanent
            }
        };

        return dispatch(AuthActionCreators.apiPost(options));
    },
    sendScreeningXmEmail: (recipientId: number, donorId: number) => async (dispatch: Dispatch<IApplicationState>): Promise<IBaseResponse> => {
        let options: IPost = {
            url: `${__API__}/recipient/send-screening-xm-email?recipientId=${recipientId}&donorId=${donorId}}`
        };

        return dispatch(AuthActionCreators.apiGet(options));
    },

    getBulkCheekSwabData: (centerId: number) => (dispatch: Dispatch<IApplicationState>): Promise<IBulkCheekSwabDataResponse> => {
        let options: IGet = {
            url: `${__API__}/center/${centerId}/bulk-cheek-swab-data`,
        }

        return dispatch(AuthActionCreators.apiGet(options));
    },
    requestBulkCheekSwabs: (centerId: number, request: IBulkCheekSwabRequest) => (dispatch: Dispatch<IApplicationState>): Promise<IBaseResponse> => {
        let { firstName, lastName, address1, address2, city, state, postalCode } = request;

        let options: IPost = {
            url: `${__API__}/center/${centerId}/bulk-cheek-swabs`,
            data: {
                firstName,
                lastName,
                address1,
                address2,
                city,
                state,
                postalCode
            }
        };

        return dispatch(AuthActionCreators.apiPost(options));
    },
    getPatientOutcomes: (centerId: number) => (dispatch: Dispatch<IApplicationState>): Promise<any> => {
        let options: IGet = {
            url: `${__API__}/center/graft-survival-info?centerId=${centerId}`,
            responseType: 'blob'
        };

        return dispatch(AuthActionCreators.apiGet(options));
    },
};

export const ActionCreators = {
    ...ActionCreatorsMain,
    ...ActionCreatorsOptnSrtrData,
    ...ActionCreatorsCenterProfile
}

export {
    IOptnSrtrDataUploadResponse, IOptnSrtrDataValidateResponse, IOptnSrtrDataProcessResponse, IOptnSrtrDataProgressResponse,
    IOptnSrtrMonthlyDataResponse, IUpdateOptnSrtrData, IOptnSrtrMonthlyDataAddResponse,
    ICenterProfileLookupDataResponse,
    ICenterAdminDataResponse,
    ICenterUnosDataResponse,
    ICenterSrtrDataResponse,
    ICenterProfileResponse,
    ICenterLabDataResponse,
    ICenterServicesDataResponse,
    ICenterSerologyDataResponse,
    ICenterAnatomyPrefsDataResponse,
    ICenterLegalDocumentsResponse,
    IManualInvoiceLineItemTaxResponse,
    IManualInvoiceLineItemLookupResponse,
    ICenterPackagingDataResponse, OrganPackaging,
    ICenterProfileLookupData, initialCenterProfileLookupData
}